import { Check, CheckCircle2, Clock3 } from 'lucide-react'
import { type ApInvoiceAssignedUser } from '@/features/invoices/types'
import { type ApInvoiceApproval, type ApInvoiceDetail } from '@/features/invoices/view-models'
import type { useInvoiceApprovalCapabilities } from '@/features/invoices/queries'
import type { useUpdateInvoice } from '@/features/invoices/mutations'
import { LoadingButton } from '@/components/loading-button'
import { Badge } from '@/components/ui/badge'
import { formatDateOnly, formatStatus } from '@/lib/utils'
import { type useInvoiceApproval } from './use-approval'

const APPROVE_CLASS = 'bg-emerald-600 text-white hover:bg-emerald-700'

export function ApprovalPath({
  status,
  approvals,
  operationsOwner,
  financeRequired,
}: {
  status: string
  approvals: ApInvoiceApproval[]
  operationsOwner?: ApInvoiceAssignedUser | null
  financeRequired: boolean
}) {
  // Operations approves an invoice over its PO first, then Accounting (USA-1702).
  const accountingComplete = ['finance_review', 'payment_ready', 'scheduled', 'processing', 'paid'].includes(
    status,
  )
  const operationsCurrent = status === 'operations_review'
  const operationsComplete =
    accountingComplete || approvals.some((approval) => approval.decision === 'approve_operations')
  const readyComplete = ['payment_ready', 'scheduled', 'processing', 'paid'].includes(status)
  const invalidated = approvals.length > 0 && ['needs_approval', 'approved_by_pc'].includes(status)
  const rows = [
    {
      label: 'Operations approval',
      detail: operationsOwner?.name || 'Project owner required',
      complete: operationsComplete,
      current: operationsCurrent,
    },
    {
      label: 'Accounting review',
      complete: accountingComplete,
      current: !accountingComplete && !operationsCurrent,
    },
    {
      label:
        financeRequired || status === 'finance_review' ? 'Finance approval' : 'Payment readiness',
      complete: readyComplete,
      current: accountingComplete && !readyComplete,
    },
  ]

  return (
    <section
      aria-labelledby="invoice-approval-path"
      className="overflow-hidden rounded-lg border bg-muted/20"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5">
        <h3 id="invoice-approval-path" className="font-semibold">
          Approval path
        </h3>
        {invalidated && <Badge variant="outline">Restarted after edit</Badge>}
      </div>
      <ol className="divide-y bg-background/80">
        {rows.map((row) => (
          <li key={row.label} className="flex min-w-0 items-center gap-3 px-3 py-2.5">
            {row.complete ? (
              <CheckCircle2 className="size-4 shrink-0 text-emerald-700" aria-hidden="true" />
            ) : (
              <Clock3
                className={`size-4 shrink-0 ${row.current ? 'text-foreground' : 'text-muted-foreground'}`}
                aria-hidden="true"
              />
            )}
            <div className="min-w-0 flex-1">
              <p
                className={`text-sm font-medium ${!row.complete && !row.current ? 'text-muted-foreground' : ''}`}
              >
                {row.label}
              </p>
              {row.detail && <p className="truncate text-xs text-muted-foreground">{row.detail}</p>}
            </div>
            <span className="text-xs text-muted-foreground">
              {row.complete ? 'Complete' : row.current ? 'Current' : 'Waiting'}
            </span>
          </li>
        ))}
      </ol>
      {approvals.length > 0 && (
        <details className="border-t px-3 py-2.5 text-xs">
          <summary className="cursor-pointer font-medium">
            Approval history ({approvals.length})
          </summary>
          <ol className="mt-2 space-y-2 text-muted-foreground">
            {approvals.map((approval) => (
              <li key={approval.uuid} className="flex flex-wrap justify-between gap-x-3 gap-y-1">
                <span>
                  {formatStatus(approval.decision)} by {formatStatus(approval.actor_role)}
                </span>
                <span>{formatDateOnly(approval.created_at.slice(0, 10))}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  )
}

export function InvoiceApprovalControls({
  invoice,
  approval,
  updateInvoice,
}: {
  invoice: ApInvoiceDetail
  approval: ReturnType<typeof useInvoiceApproval>
  updateInvoice: ReturnType<typeof useUpdateInvoice>
}) {
  const {
    approvalCapabilities,
    decideApproval,
    approveAccounting,
    quickApprove,
    runApprovalDecision,
  } = approval
  return (
    <>
      {approvalCapabilities.data?.enforcement_enabled === false &&
        invoice.status === 'needs_approval' && (
          <LoadingButton
            size="sm"
            className={APPROVE_CLASS}
            loading={updateInvoice.isPending}
            loadingText="Approving..."
            onClick={quickApprove}
          >
            <Check className="h-3.5 w-3.5" /> Approve
          </LoadingButton>
        )}
      {approvalCapabilities.data?.enforcement_enabled === true &&
        approvalCapabilities.data.allowed_decisions.includes('approve_accounting') && (
          <LoadingButton
            size="sm"
            className={APPROVE_CLASS}
            loading={decideApproval.isPending}
            loadingText="Approving..."
            onClick={approveAccounting}
          >
            <Check className="h-3.5 w-3.5" />
            Approve accounting review
          </LoadingButton>
        )}
      {approvalCapabilities.data?.allowed_decisions.includes('approve_operations') && (
        <LoadingButton
          size="sm"
          className={APPROVE_CLASS}
          loading={decideApproval.isPending}
          loadingText="Approving..."
          onClick={() => runApprovalDecision('approve_operations', 'Operations approval recorded')}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Approve as Operations
        </LoadingButton>
      )}
      {approvalCapabilities.data?.allowed_decisions.includes('approve_finance') && (
        <LoadingButton
          size="sm"
          className={APPROVE_CLASS}
          loading={decideApproval.isPending}
          loadingText="Approving..."
          onClick={() => runApprovalDecision('approve_finance', 'Finance approval recorded')}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Approve as finance
        </LoadingButton>
      )}
    </>
  )
}

export function InvoiceApprovalNotices({
  approvalCapabilities,
}: {
  approvalCapabilities: ReturnType<typeof useInvoiceApprovalCapabilities>
}) {
  return (
    <>
      {approvalCapabilities.isError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          Approval controls are unavailable. Refresh before changing this invoice's status.
        </div>
      )}
      {approvalCapabilities.data?.enforcement_enabled &&
        (approvalCapabilities.data.blocking_reasons.length > 0 ||
          approvalCapabilities.data.finance_reasons.length > 0 ||
          approvalCapabilities.data.self_approval_conflict) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">Approval controls</p>
            {approvalCapabilities.data.blocking_reasons.length > 0 && (
              <p>
                Resolve:{' '}
                {approvalCapabilities.data.blocking_reasons.join(', ').replaceAll('_', ' ')}
              </p>
            )}
            {approvalCapabilities.data.finance_reasons.length > 0 && (
              <p>
                Finance review:{' '}
                {approvalCapabilities.data.finance_reasons.join(', ').replaceAll('_', ' ')}
              </p>
            )}
            {approvalCapabilities.data.self_approval_conflict && (
              <p>
                A separate authorized reviewer is required after preparation or a material edit.
              </p>
            )}
          </div>
        )}
    </>
  )
}
